/**
 * Catalog Install Search
 *
 * Search-first installer for MCP servers and Losi Skills.
 *
 * Primary responsibilities:
 * - Search MCP and Skill catalogs before installation
 * - Install the selected result and surface OAuth/setup next steps
 *
 * Dependencies:
 * - MCP install API
 * - Skill install API
 */

'use client'

import { toPublicAppUrl } from '@/lib/url/public-app-url'
import { FormEvent, useEffect, useState } from 'react'
import {
  BookOpenCheck,
  CheckCircle2,
  LockKeyhole,
  Plug,
  Plus,
  Search,
  ServerCog
} from 'lucide-react'
import { toast } from 'sonner'

import { getMcpSetupCard, type McpSetupCard } from '@/components/integrations/mcp-setup-card'
import { getSkillSetupCard, type SkillSetupCard } from '@/components/integrations/skill-setup-card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export type CatalogInstallKind = 'mcp' | 'skills'

export type CatalogSearchResult = {
  authType: string | null
  description: string
  id: string
  name: string
  registryName: string | null
  repository: string | null
  repositoryRef: string | null
  serverUrl: string | null
  skillPath: string | null
  source: string
  sourceType: string | null
  sourceUrl: string | null
  title: string
  warnings: string[]
}

type CatalogInstallSearchProps = {
  className?: string
  initialQuery?: string
  kind: CatalogInstallKind
  onInstalled?: () => Promise<void> | void
  onMcpSetup?: (setup: McpSetupCard) => void
  onOpenOAuth?: (url: string, integrationId: string) => void
  onSkillSetup?: (setup: SkillSetupCard) => void
  workspaceId?: string | null
}

type InstalledResultState = {
  actionUrl: string | null
  label: string
  setup: McpSetupCard | SkillSetupCard | null
  status: 'added' | 'authenticate' | 'connect' | 'review'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function getStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.flatMap((item) => (typeof item === 'string' && item.trim() ? [item.trim()] : []))
    : []
}

function isUrlLike(value: string) {
  return /^https?:\/\//i.test(value.trim())
}

function getTitleCaseLabel(value: string | null, fallback: string) {
  if (!value) {
    return fallback
  }

  const normalized = value.trim().replace(/[_-]+/g, ' ').toLowerCase()

  if (normalized === 'oauth') return 'OAuth'
  if (normalized === 'api key') return 'API key'
  if (normalized === 'apikey') return 'API key'
  if (normalized === 'none') return 'No auth'
  if (normalized === 'github') return 'GitHub'
  if (normalized === 'url') return 'URL'

  return normalized.replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function getSearchMessage(value: unknown, fallback: string) {
  if (!isRecord(value)) {
    return fallback
  }

  const message = value.message ?? value.error

  return typeof message === 'string' && message.trim() ? message : fallback
}

function getMcpSearchResults(value: unknown): CatalogSearchResult[] {
  if (!isRecord(value) || !Array.isArray(value.servers)) {
    return []
  }

  return value.servers.flatMap((server, index) => {
    if (!isRecord(server)) {
      return []
    }

    const name = getString(server.name) ?? getString(server.registryName)
    const serverUrl = getString(server.serverUrl) ?? getString(server.server_url)
    const sourceUrl = getString(server.sourceUrl) ?? getString(server.source_url) ?? serverUrl
    const title = getString(server.title) ?? name ?? serverUrl

    if (!title) {
      return []
    }

    return [
      {
        authType: getString(server.authType) ?? getString(server.auth_type),
        description:
          getString(server.description) ??
          'Losi found this MCP server. Review the source before connecting it.',
        id: getString(server.registryName) ?? name ?? serverUrl ?? `mcp-${index}`,
        name: name ?? title,
        registryName: getString(server.registryName) ?? getString(server.registry_name),
        repository: null,
        repositoryRef: null,
        serverUrl,
        skillPath: null,
        source: getString(server.source) ?? 'registry',
        sourceType: null,
        sourceUrl,
        title,
        warnings: getStringArray(server.warnings)
      }
    ]
  })
}

function getSkillSearchResults(value: unknown): CatalogSearchResult[] {
  if (!isRecord(value) || !Array.isArray(value.skills)) {
    return []
  }

  return value.skills.flatMap((skill, index) => {
    if (!isRecord(skill)) {
      return []
    }

    const name = getString(skill.name)
    const title = getString(skill.displayName) ?? getString(skill.display_name) ?? name

    if (!name || !title) {
      return []
    }

    return [
      {
        authType: null,
        description:
          getString(skill.description) ??
          'Losi found this skill. Review the source before enabling it.',
        id: [name, getString(skill.sourceUrl), getString(skill.skillPath), index]
          .filter(Boolean)
          .join(':'),
        name,
        registryName: null,
        repository: getString(skill.repository),
        repositoryRef: getString(skill.repositoryRef) ?? getString(skill.repository_ref) ?? null,
        serverUrl: null,
        skillPath: getString(skill.skillPath) ?? getString(skill.skill_path),
        source: getString(skill.sourceType) ?? getString(skill.source_type) ?? 'marketplace',
        sourceType: getString(skill.sourceType) ?? getString(skill.source_type),
        sourceUrl: getString(skill.sourceUrl) ?? getString(skill.source_url),
        title,
        warnings: getStringArray(skill.warnings)
      }
    ]
  })
}

async function readJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null)
}

function openOAuthPopup(url: string, onInstalled?: () => Promise<void> | void) {
  const popup = window.open(toPublicAppUrl(url), 'losi-integration-oauth', 'height=760,width=720')

  if (!popup) {
    toast.error('Allow popups to authenticate this MCP')
    return
  }

  popup.focus()
  toast('Complete authentication in the popup')

  const timer = window.setInterval(() => {
    if (!popup.closed) {
      return
    }

    window.clearInterval(timer)
    void onInstalled?.()
  }, 1000)
}

function getSearchEndpoint(kind: CatalogInstallKind, query: string) {
  const params = new URLSearchParams()

  if (kind === 'mcp' && isUrlLike(query)) {
    params.set('server_url', query)
  } else {
    params.set('query', query)
  }

  return kind === 'mcp'
    ? `/api/integrations/mcp/install?${params.toString()}`
    : `/api/integrations/skills/install?${params.toString()}`
}

function getResultBadgeLabel(kind: CatalogInstallKind, result: CatalogSearchResult) {
  if (kind === 'mcp') {
    return getTitleCaseLabel(result.authType, 'Auth')
  }

  return getTitleCaseLabel(result.sourceType, 'Source')
}

function getInstallButtonLabel(kind: CatalogInstallKind, result: CatalogSearchResult) {
  if (kind === 'skills') {
    return 'Add skill'
  }

  return result.authType?.toLowerCase() === 'oauth' ? 'Authenticate' : 'Add MCP'
}

function getInstallButtonIcon(kind: CatalogInstallKind, result: CatalogSearchResult) {
  if (kind === 'mcp' && result.authType?.toLowerCase() === 'oauth') {
    return LockKeyhole
  }

  return Plus
}

function getInstalledResultIcon(status: InstalledResultState['status']) {
  if (status === 'added') return CheckCircle2
  if (status === 'authenticate') return LockKeyhole
  if (status === 'connect') return Plug

  return BookOpenCheck
}

function getInstalledMcpState(setup: McpSetupCard | null): InstalledResultState {
  if (setup?.authCard?.actionUrl) {
    return {
      actionUrl: setup.authCard.actionUrl,
      label: 'Authenticate MCP',
      setup,
      status: 'authenticate'
    }
  }

  if (setup?.authCard?.cardState === 'discovered' || setup?.authCard?.cardState === 'connected') {
    return {
      actionUrl: null,
      label: 'Added',
      setup,
      status: 'added'
    }
  }

  return {
    actionUrl: null,
    label: 'Connect MCP',
    setup,
    status: 'connect'
  }
}

function getInstalledSkillState(setup: SkillSetupCard | null): InstalledResultState {
  if (setup?.cardState === 'installed' || setup?.cardState === 'enabled') {
    return {
      actionUrl: null,
      label: 'Added',
      setup,
      status: 'added'
    }
  }

  return {
    actionUrl: null,
    label: 'Review skill',
    setup,
    status: 'review'
  }
}

export function CatalogInstallSearch({
  className,
  initialQuery = '',
  kind,
  onInstalled,
  onMcpSetup,
  onOpenOAuth,
  onSkillSetup,
  workspaceId
}: CatalogInstallSearchProps) {
  const [query, setQuery] = useState(initialQuery.trim())
  const [results, setResults] = useState<CatalogSearchResult[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [installingResultId, setInstallingResultId] = useState<string | null>(null)
  const [installedResults, setInstalledResults] = useState<Record<string, InstalledResultState>>({})
  const isMcp = kind === 'mcp'
  const Icon = isMcp ? ServerCog : BookOpenCheck

  useEffect(() => {
    setQuery(initialQuery.trim())
    setResults([])
    setHasSearched(false)
    setInstallingResultId(null)
    setInstalledResults({})
  }, [initialQuery, kind])

  async function searchCatalog(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault()

    const nextQuery = query.trim()

    if (!nextQuery) {
      toast.error(isMcp ? 'Search for an MCP first' : 'Search for a skill first')
      return
    }

    setIsSearching(true)
    setHasSearched(true)
    setInstalledResults({})

    try {
      const response = await fetch(getSearchEndpoint(kind, nextQuery), {
        credentials: 'same-origin'
      })
      const data = await readJson(response)

      if (!response.ok) {
        throw new Error(getSearchMessage(data, 'Search failed'))
      }

      setResults(isMcp ? getMcpSearchResults(data) : getSkillSearchResults(data))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Search failed')
    } finally {
      setIsSearching(false)
    }
  }

  function updateQuery(nextQuery: string) {
    setQuery(nextQuery)

    if (!hasSearched && results.length === 0) {
      return
    }

    setResults([])
    setHasSearched(false)
    setInstalledResults({})
  }

  function continueInstalledResult(result: CatalogSearchResult, installed: InstalledResultState) {
    if (installed.status === 'added') {
      return
    }

    if (installed.status === 'authenticate' && installed.actionUrl) {
      if (onOpenOAuth) {
        onOpenOAuth(installed.actionUrl, 'mcp')
      } else {
        openOAuthPopup(installed.actionUrl, onInstalled)
      }

      return
    }

    if (kind === 'mcp') {
      onMcpSetup?.(
        (installed.setup as McpSetupCard | null) ?? {
          authOptions: [],
          description: result.description,
          registryUrl: result.sourceUrl ?? '',
          requestedName: result.name,
          serverQuery: result.name,
          title: `Connect ${result.title}`
        }
      )
      return
    }

    if (installed.setup) {
      onSkillSetup?.(installed.setup as SkillSetupCard)
    }
  }

  async function installResult(result: CatalogSearchResult) {
    if (!workspaceId) {
      toast.error('Choose a workspace before adding this integration')
      return
    }

    setInstallingResultId(result.id)

    try {
      const body: Record<string, unknown> =
        kind === 'mcp'
          ? {
              query: result.name,
              server_name: result.registryName ?? result.name,
              server_url: result.serverUrl ?? undefined,
              workspace_id: workspaceId
            }
          : {
              query: result.name,
              repository: result.repository ?? undefined,
              repository_ref: result.repositoryRef ?? undefined,
              skill_name: result.name,
              skill_path: result.skillPath ?? undefined,
              source_url: result.sourceUrl ?? undefined,
              workspace_id: workspaceId
            }
      const endpoint =
        kind === 'mcp' ? '/api/integrations/mcp/install' : '/api/integrations/skills/install'
      const response = await fetch(endpoint, {
        body: JSON.stringify(body),
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        method: 'POST'
      })
      const data = await readJson(response)

      if (!response.ok) {
        throw new Error(getSearchMessage(data, 'Failed to add integration'))
      }

      if (kind === 'mcp') {
        const setup = getMcpSetupCard(data)
        const installedState = getInstalledMcpState(setup)

        setInstalledResults((current) => ({
          ...current,
          [result.id]: installedState
        }))

        await onInstalled?.()

        if (setup?.authCard?.actionUrl) {
          if (onOpenOAuth) {
            onOpenOAuth(setup.authCard.actionUrl, 'mcp')
          } else {
            openOAuthPopup(setup.authCard.actionUrl, onInstalled)
          }

          return
        }

        if (setup) {
          onMcpSetup?.(setup)
        }

        toast.success('MCP setup ready')
        return
      }

      const setup = getSkillSetupCard(data)
      const installedState = getInstalledSkillState(setup)

      setInstalledResults((current) => ({
        ...current,
        [result.id]: installedState
      }))

      await onInstalled?.()

      if (setup) {
        onSkillSetup?.(setup)
      }

      toast.success('Skill setup ready')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add integration')
    } finally {
      setInstallingResultId(null)
    }
  }

  return (
    <div className={cn('flex min-h-0 flex-col gap-4', className)}>
      <form className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]" onSubmit={searchCatalog}>
        <div className="relative min-w-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label={
              isMcp
                ? 'Search or name an MCP server'
                : 'Search skills marketplace or paste GitHub skill URL'
            }
            className="pl-9"
            onChange={(event) => updateQuery(event.target.value)}
            placeholder={
              isMcp
                ? 'Search MCP servers or paste a server URL...'
                : 'Search skills marketplace or paste GitHub skill URL...'
            }
            value={query}
          />
        </div>
        <Button className="justify-center" loading={isSearching} type="submit">
          <Search className="size-4" />
          Search
        </Button>
      </form>

      <div className="min-h-[14rem] overflow-hidden rounded-lg border border-border bg-muted/20">
        <div className="flex items-center justify-between gap-3 border-b border-border/70 px-3 py-2">
          <div className="flex min-w-0 items-center gap-2 text-xs font-medium text-muted-foreground">
            <Icon className="size-4 shrink-0" />
            <span>{isMcp ? 'MCP search results' : 'Skill search results'}</span>
          </div>
          <Badge className="border-border bg-background text-muted-foreground" variant="outline">
            {results.length}
          </Badge>
        </div>

        <div className="max-h-[min(52dvh,28rem)] overflow-y-auto p-3">
          {isSearching ? (
            <div className="flex min-h-40 items-center justify-center text-sm text-muted-foreground">
              Searching...
            </div>
          ) : null}

          {!isSearching && hasSearched && results.length === 0 ? (
            <div className="flex min-h-40 items-center justify-center text-center text-sm text-muted-foreground">
              No results found. Try a more specific name or URL.
            </div>
          ) : null}

          {!isSearching && !hasSearched ? (
            <div className="flex min-h-40 items-center justify-center text-center text-sm text-muted-foreground">
              Search first, then choose exactly what to add.
            </div>
          ) : null}

          {!isSearching && results.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {results.map((result) => (
                <SearchResultCard
                  installed={installedResults[result.id] ?? null}
                  installing={installingResultId === result.id}
                  key={result.id}
                  kind={kind}
                  onContinue={() => {
                    const installed = installedResults[result.id]
                    if (installed) continueInstalledResult(result, installed)
                  }}
                  onInstall={() => {
                    void installResult(result)
                  }}
                  result={result}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function SearchResultCard({
  installed,
  installing,
  kind,
  onContinue,
  onInstall,
  result
}: {
  installed: InstalledResultState | null
  installing: boolean
  kind: CatalogInstallKind
  onContinue: () => void
  onInstall: () => void
  result: CatalogSearchResult
}) {
  const InstallIcon = installed
    ? getInstalledResultIcon(installed.status)
    : getInstallButtonIcon(kind, result)
  const isAdded = installed?.status === 'added'

  return (
    <article className="flex min-h-[172px] flex-col gap-3 rounded-lg border border-border bg-background p-3 shadow-sm">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="line-clamp-2 text-sm font-semibold">{result.title}</h3>
          <p className="mt-1 line-clamp-3 text-xs leading-5 text-muted-foreground">
            {result.description}
          </p>
        </div>
        <Badge
          className={cn(
            'shrink-0',
            installed
              ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
              : 'border-primary/25 bg-primary/10 text-primary'
          )}
          variant="outline"
        >
          {installed ? 'Added' : getResultBadgeLabel(kind, result)}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-1">
        <Badge className="border-border bg-muted/70 text-muted-foreground" variant="outline">
          {getTitleCaseLabel(result.source, 'Source')}
        </Badge>
        {result.repository ? (
          <Badge
            className="max-w-full truncate border-border bg-muted/70 text-muted-foreground"
            variant="outline"
          >
            {result.repository}
          </Badge>
        ) : null}
      </div>

      {result.warnings.slice(0, 1).map((warning) => (
        <p className="line-clamp-2 text-xs text-amber-700" key={warning}>
          {warning}
        </p>
      ))}

      <Button
        className="mt-auto w-full justify-center gap-2"
        disabled={isAdded}
        loading={installing}
        onClick={installed ? onContinue : onInstall}
        size="sm"
        type="button"
        variant={installed ? 'outline' : 'default'}
      >
        <InstallIcon className="size-4" />
        {installed ? installed.label : getInstallButtonLabel(kind, result)}
      </Button>
    </article>
  )
}
